<template>
  <div
    class="htnova-dashboard"
    v-loading="loading"
    element-loading-background="rgba(0, 0, 0, 0.5)"
    :element-loading-text="$t('loading') + '...'"
  >
    <MapLibregl @loaded="mapLoaded" :theme="theme" />
    <div class="content">
      <div class="header">
        <div class="refresh">
          <el-button type="text" @click="refresh">{{ $t('refresh') }}</el-button>
        </div>
      </div>
      <div class="statis">
        <div class="left">
          <BorderWrap v-for="(chart, index) in leftCharts" v-bind="chart" :key="index" />
        </div>
        <div class="middle">
          <div class="tabs-wrap" :class="$i18n.locale">
            <TabWrap :label="$t('deviceCount')" icon="icon-string" unit="" :value="deviceTotal" />
            <TabWrap
              icon="icon-headset"
              :label="$t('alarmCount')"
              :unit="$t('times')"
              :value="alarmCount"
            />
            <TabWrap
              v-if="hasDetection"
              icon="icon-detection"
              :label="$t('checkCount')"
              :unit="$t('times')"
              :value="detectionCount"
            />
          </div>

          <BorderWrap size="large" v-if="hasDetection">
            <div class="h-table-wrap">
              <div class="thead">
                <div class="column" v-for="item in table.thead" :key="item.prop">
                  {{ $t(item.label) }}
                </div>
              </div>
              <div class="tbody">
                <div class="empty" v-if="table.data.length === 0">
                  {{ $t('暂无数据') }}
                </div>
                <div class="row" v-else v-for="(row, index) in table.data" :key="`row-${index}`">
                  <div class="column" v-for="(item, i) in table.thead" :key="`r${index}col-${i}`">
                    {{ row[item.prop] }}
                  </div>
                </div>
              </div>
            </div>
          </BorderWrap>
        </div>
        <div class="right" v-if="hasDetection">
          <BorderWrap v-for="(chart, index) in rightCharts" v-bind="chart" :key="index" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BorderWrap from './components/border-wrap';
import TabWrap from './components/tab-wrap';
import MapLibregl from '@/components/maplibre/index.vue';

import { infoContent, colors, bmap2CGCS2000, colorMap } from '@/utils/bmapUtil';

import { makeDoughnut, makeLine, makeBar, setData } from './utils';
import {
  getAlarmList,
  getAlarmCount,
  getYearAlarm,
  getYearAlarmTrend,
  getDetectionList,
  getDetectionCount,
  getYearDetection,
  getYearDetectionTrend
} from './utils/data';
import { awaitTo, timer } from '@/utils/utils';

import ctt from '@/utils/const';

export default {
  components: { MapLibregl, BorderWrap, TabWrap },
  data() {
    return {
      loading: true,

      interval: null,
      alarmCount: 0,
      dayAlarm: makeDoughnut(),
      yearAlarm: makeBar({ color: ['#04d7ff'], xLabelRotate: 20 }),
      yearAlarmTrend: makeLine({ color: ['#a759ff'] }),

      detectionCount: 0,
      dayDetection: makeDoughnut(),
      yearDetection: makeBar({ xLabelRotate: 0, color: ['#b761ff'] }),
      yearDetectionTrend: makeLine({ color: ['#ff9747'] }),
      markerMap: {},
      infoWindow: null,
      timer: null,
      table: {
        thead: [
          { prop: 'beginTimeD', label: '检测时间' },
          { prop: 'name', label: '设备名称' },
          { prop: 'sn', label: '设备编号' },
          { prop: 'officeName', label: '所属机构' },
          { prop: 'alarmContent', label: '检测结果' }
        ],
        data: []
      }
    };
  },
  sockets: {
    '/socket/detect/detail'(res) {
      if (res.runMode !== ctt.DETECT_DEVICE) return;
      try {
        const { mods } = res;
        const alarmContent = mods.detectionMod.alarmContent || '--';
        this.table.data.push({ ...res, alarmContent });
        this.scrollToBottom();
      } catch (error) {
        console.error('[dashboard-detect-detail]:', error);
      }
    }
  },
  computed: {
    ...mapState('socket', ['devicesSourseMap', 'deviceIdMap']),
    ...mapState('admin', ['userInfo']),
    ...mapState('app', ['theme']),
    deviceTotal() {
      return `${this.devicesSourseMap.filter(e => e.state >= 0).length}/${
        this.devicesSourseMap.length
      }`;
    },
    leftCharts() {
      const year = new Date().getUTCFullYear();
      return [
        { title: this.$t('dayAlarmCount'), options: this.dayAlarm },
        { title: `${year} ${this.$t('yearAlarmCount')}`, options: this.yearAlarm },
        { title: `${year} ${this.$t('yearAlarmTrend')}`, options: this.yearAlarmTrend }
      ];
    },
    rightCharts() {
      const year = new Date().getUTCFullYear();
      return [
        { title: this.$t('dayCheckCount'), options: this.dayDetection },
        { title: `${year} ${this.$t('yearCheckCount')}`, options: this.yearDetection },
        {
          title: `${year} ${this.$t('yearCheckTrend')}`,
          options: this.yearDetectionTrend
        }
      ];
    },
    hasDetection() {
      return this.userInfo.displayType === 2;
    }
  },
  methods: {
    langChangeHandler() {
      this.initChart();
    },
    refresh() {
      this.initChart();
    },
    mapLoaded(map) {
      this.map = map;
      this.$nextTick(() => {
        this.initMarker();
        this.intervalUpdate();
      });
    },
    async initChart() {
      this.loading = true;
      const [err, res] = await awaitTo(
        Promise.all([
          getAlarmList(),
          getYearAlarm(),
          getYearAlarmTrend(),
          getDetectionList(),
          getYearDetection(),
          getYearDetectionTrend()
        ])
      );
      if (err) {
        console.log(`[dashboard-initChart]: `, '数据加载失败');
      }

      const [
        alarmList,
        yearAlarm,
        yearAlarmTrend,
        detectionList,
        yearDetection,
        yearDetectionTrend
      ] = res;

      // 报警
      this.alarmCount = getAlarmCount(alarmList);
      setData(this.dayAlarm, alarmList);
      setData(this.yearAlarm, yearAlarm);
      setData(this.yearAlarmTrend, yearAlarmTrend);

      // 检测
      this.detectionCount = getDetectionCount(detectionList);
      setData(this.dayDetection, detectionList);
      setData(this.yearDetection, yearDetection);
      setData(this.yearDetectionTrend, yearDetectionTrend);

      this.loading = false;
    },
    scrollToBottom() {
      const el = document.querySelector('.customs-dashboard .tbody');
      const toBottom = () => {
        el.scrollTo({
          top: el.scrollHeight - el.clientHeight,
          left: 0,
          behavior: 'smooth'
        });
      };
      toBottom();
      if (this.table.data.length > 20) {
        timer(() => {
          this.table.data.splice(0, this.table.data.length - 10);
          toBottom();
        }, 1000);
      }
    },
    initMarker() {
      const devices = this.devicesSourseMap;
      const coords = [];

      devices.forEach(e => {
        try {
          if (!e.longitude || !e.latitude) return;

          const coord = bmap2CGCS2000([e.longitude, e.latitude]);
          coords.push(coord);

          const marker = this.map.addMarker({
            markerOpts: { color: colorMap[e.state] },
            coord,
            html: this.showInfoWindow(e)
          });

          this.markerMap[e.sn] = {
            ...e,
            marker
          };
        } catch (_error) {
          // console.error(_error);

          const marker = this.map.addMarker({
            markerOpts: { color: colorMap[e.state] },
            coord: [0, 0],
            html: this.showInfoWindow(e)
          });

          this.markerMap[e.sn] = {
            ...e,
            marker
          };
        }
      });

      // 初始化视角：默认定位到设备位置，多设备时全部显示
      if (coords.length) {
        this.$nextTick(() => {
          this.map.fitCoordinates(coords, { padding: 80, maxZoom: 15 });
        });
      }
    },
    patchMarker(device) {
      try {
        const marker = this.markerMap[device.sn];

        if (!device.longitude || !device.latitude) return;

        let hasChanges = true;

        const coord = bmap2CGCS2000([device.longitude, device.latitude]);

        if (marker) {
          const oldDevice = marker;

          hasChanges =
            oldDevice.name !== device.name ||
            oldDevice.state !== device.state ||
            oldDevice.longitude !== device.longitude ||
            oldDevice.latitude !== device.latitude;
        }

        // 如果没有变化，则不更新
        if (!hasChanges) return;

        this.markerMap[device.sn] = {
          ...device,
          marker: this.map.addMarker({
            markerOpts: { color: colorMap[device.state] },
            coord,
            html: this.showInfoWindow(device)
          })
        };
      } catch (_error) {
        // console.error('[更新Marker]', _error);
      }
    },
    intervalUpdate() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        const devices = this.devicesSourseMap;
        devices.forEach(e => {
          this.patchMarker(e);
        });
      }, 3000);
    },
    showInfoWindow({ name, state = -1, parentId, sn, deviceType }) {
      return infoContent({
        colors,
        parentId,
        deviceIdMap: this.deviceIdMap,
        name,
        state,
        sn,
        deviceType,
        router: this.$router
      });
    }
  },
  created() {
    this.initChart();
    // this.initTable();
  },
  beforeDestroy() {
    clearInterval(this.interval);
    clearInterval(this.timer);
  }
};
</script>

<style lang="scss">
.htnova-dashboard {
  width: 100%;
  height: 100%;
  position: relative;

  & > .content {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    z-index: 9;

    height: 100%;
    width: 100%;

    pointer-events: none;

    & > .header {
      display: flex;
      justify-content: flex-end;

      height: 30px;

      padding-right: 30px;

      .refresh {
        pointer-events: auto;
      }
    }
    & > .statis {
      width: 100%;
      height: calc(100% - 30px);

      padding: 10px 10px 20px;

      box-sizing: border-box;

      display: flex;
      justify-content: flex-start;

      & > .left {
        width: calc(25% - 6px);
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .border-wrap {
          height: calc(33% - 6px);
          pointer-events: auto;
        }
      }

      & > .right {
        width: calc(25% - 6px);
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .border-wrap {
          height: calc(33% - 6px);
          pointer-events: auto;
        }
      }

      & > .middle {
        width: calc(50% - 6px);
        margin: 0 9px;
        height: 100%;

        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .border-wrap {
          width: 100%;
          height: calc(32% - 6px);
          pointer-events: auto;
        }

        .h-table-wrap {
          height: 100%;
          font-size: 12px;

          .thead {
            display: flex;
            justify-content: space-between;

            color: #04d7ff;
            padding: 14px 0;
          }

          .tbody {
            height: calc(100% - 50px);
            overflow-y: scroll;
            margin-right: -18px;

            color: #eee;

            .empty {
              height: 70%;
              display: flex;
              justify-content: center;
              align-items: center;
              color: #04d7ff;
            }

            .row {
              padding: 8px 0;

              display: flex;
              justify-content: space-between;
            }
          }

          .column {
            flex: 1;
            text-align: center;
          }
          .column:nth-of-type(2) {
            width: 15%;
          }
          .column:nth-of-type(3) {
            width: 25%;
          }
        }
        .tabs-wrap {
          .h-tab-wrap {
            margin-bottom: 10px;
          }
        }
      }
    }
  }
}
</style>
